/**
 * useFinancePayroll — Issue #371.
 *
 * 调用整本工资总表导入、批次审批、行级 hold/payout、reveal 等接口。
 * 全部通过 @jinzhuan/api-client；禁止伪状态。
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
  type ApiComponents,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type PayrollBatchSummaryDto = ApiComponents['schemas']['PayrollBatchSummaryDto'];
export type PayrollBatchDetailDto = ApiComponents['schemas']['PayrollBatchDetailDto'];
export type PayrollBatchListResponseDto =
  ApiComponents['schemas']['PayrollBatchListResponseDto'];
export type PayrollImportResponseDto =
  ApiComponents['schemas']['PayrollImportResponseDto'];
export type ImportPayrollWorkbookDto =
  ApiComponents['schemas']['ImportPayrollWorkbookDto'];
export type PayrollEmployeeRowSummaryDto =
  ApiComponents['schemas']['PayrollEmployeeRowSummaryDto'];
export type PayrollRowDetailDto = ApiComponents['schemas']['PayrollRowDetailDto'];
export type PayrollItemDto = ApiComponents['schemas']['PayrollItemDto'];
export type PayrollImportErrorDto =
  ApiComponents['schemas']['PayrollImportErrorDto'];
export type PayrollEventDto = ApiComponents['schemas']['PayrollEventDto'];
export type PayrollPayoutRecordDto =
  ApiComponents['schemas']['PayrollPayoutRecordDto'];
export type PayrollRevealResponseDto =
  ApiComponents['schemas']['PayrollRevealResponseDto'];
export type PayrollBatchHoldRefreshResponseDto =
  ApiComponents['schemas']['PayrollBatchHoldRefreshResponseDto'];
export type PayrollFileReferenceDto =
  ApiComponents['schemas']['PayrollFileReferenceDto'];

export interface ApiError {
  message: string;
  code?: string;
}

/**
 * Codex audit fix #2 — every raw `fetch()` `.json()` must be preceded by a
 * `content-type` guard so an HTML proxy / 502 / WAF page is never silently
 * parsed as JSON. The typed api-client centralizes this for normal endpoints;
 * the multipart upload path is the only place admin-web touches `fetch()`
 * directly.
 */
function isJsonContentType(header: string | null): boolean {
  if (!header) return false;
  const lowered = header.toLowerCase().split(';')[0]!.trim();
  return lowered === 'application/json' || lowered.endsWith('+json');
}

function toApiError(err: unknown, status?: number): ApiError {
  const effective =
    status ?? (err instanceof ApiClientError ? err.status : undefined);
  if (effective === 401) return { message: '会话已失效', code: 'UNAUTHENTICATED' };
  if (effective === 403) return { message: '没有权限', code: 'FORBIDDEN' };
  if (effective === 404) return { message: '记录不存在', code: 'NOT_FOUND' };
  if (effective === 409) return { message: '数据冲突，请刷新重试', code: 'CONFLICT' };
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

// ─── Batch list / detail ─────────────────────────────────────────────────────

export interface ListBatchesFilter {
  status?: string;
  periodYear?: number;
  periodMonth?: number;
  search?: string;
  page?: number;
  limit?: number;
}

export function usePayrollBatches() {
  const [data, setData] = useState<PayrollBatchListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter?: ListBatchesFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.periodYear !== undefined) query.periodYear = filter.periodYear;
      if (filter?.periodMonth !== undefined)
        query.periodMonth = filter.periodMonth;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/payroll/batches',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function usePayrollBatchDetail() {
  const [data, setData] = useState<PayrollBatchDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/payroll/batches/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      setData(ok ?? null);
      return ok ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchDetail };
}

// ─── Batch actions ───────────────────────────────────────────────────────────

export function usePayrollBatchActions() {
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const importWorkbook = useCallback(async (body: ImportPayrollWorkbookDto) => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { data: ok, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/payroll/batches/import',
        { body },
      );
      if (apiErr) {
        setActionError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return ok ?? null;
    } catch (e) {
      setActionError(toApiError(e));
      return null;
    } finally {
      setActionLoading(false);
    }
  }, []);

  /**
   * Issue #371 audit fix — direct `.xlsx` workbook upload via multipart
   * form-data. The server parses the workbook into sheets / rows / items and
   * runs the same required-sheet validation as the JSON path. Used by the
   * default admin-web "导入整本工资总表" button.
   *
   * The OpenAPI typed client cannot model `multipart/form-data` upload
   * payloads, so this path uses raw `fetch()`. Per the response-defense rule
   * for raw fetch calls (Codex audit #2): every `res.json()` is preceded by a
   * `content-type` JSON guard so an HTML proxy / WAF page never gets fed into
   * `JSON.parse` silently.
   */
  const uploadWorkbookFile = useCallback(
    async (input: {
      file: File;
      title: string;
      periodYear: number;
      periodMonth: number;
      payDate?: string;
      batchNo?: string;
      remarks?: string;
    }): Promise<PayrollImportResponseDto | null> => {
      setActionLoading(true);
      setActionError(null);
      try {
        const form = new FormData();
        form.append('file', input.file, input.file.name);
        form.append('title', input.title);
        form.append('periodYear', String(input.periodYear));
        form.append('periodMonth', String(input.periodMonth));
        if (input.payDate) form.append('payDate', input.payDate);
        if (input.batchNo) form.append('batchNo', input.batchNo);
        if (input.remarks) form.append('remarks', input.remarks);
        const token = getStoredAccessToken();
        const res = await fetch(
          '/api/v1/finance/payroll/batches/upload-workbook',
          {
            method: 'POST',
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            body: form,
          },
        );
        if (!res.ok) {
          const status = res.status;
          let message = `HTTP ${status}`;
          if (isJsonContentType(res.headers.get('content-type'))) {
            try {
              const payload: unknown = await res.json();
              if (
                typeof payload === 'object' &&
                payload !== null &&
                'message' in payload &&
                typeof (payload as { message: unknown }).message === 'string'
              ) {
                message = (payload as { message: string }).message;
              }
            } catch {
              // body parse failure; keep status-derived message
            }
          } else {
            message = `工资总表上传失败 (HTTP ${status})：服务器返回非 JSON 响应`;
          }
          setActionError(toApiError(new Error(message), status));
          return null;
        }
        if (!isJsonContentType(res.headers.get('content-type'))) {
          throw new Error(
            `工资总表上传失败 (HTTP ${res.status})：服务器未返回 JSON 响应`,
          );
        }
        const ok = (await res.json()) as PayrollImportResponseDto;
        return ok;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const submitForHrReview = useCallback(
    async (id: string, expectedRecordVersion: number) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/batches/{id}/submit-for-hr-review',
          {
            params: { path: { id } },
            body: { expectedRecordVersion },
          },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const hrReview = useCallback(
    async (
      id: string,
      input: { expectedRecordVersion: number; reason?: string; reviewerName?: string },
    ) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/batches/{id}/hr-review',
          { params: { path: { id } }, body: input },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const financeReview = useCallback(
    async (
      id: string,
      input: { expectedRecordVersion: number; reason?: string; reviewerName?: string },
    ) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/batches/{id}/finance-review',
          { params: { path: { id } }, body: input },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const startPayout = useCallback(
    async (id: string, expectedRecordVersion: number) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/batches/{id}/start-payout',
          { params: { path: { id } }, body: { expectedRecordVersion } },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const refreshHolds = useCallback(async (id: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { data: ok, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/payroll/batches/{id}/refresh-holds',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setActionError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return ok ?? null;
    } catch (e) {
      setActionError(toApiError(e));
      return null;
    } finally {
      setActionLoading(false);
    }
  }, []);

  const rejectBatch = useCallback(
    async (id: string, input: { expectedRecordVersion: number; reason: string }) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/batches/{id}/reject',
          { params: { path: { id } }, body: input },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const cancelBatch = useCallback(
    async (id: string, input: { expectedRecordVersion: number; reason: string }) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/batches/{id}/cancel',
          { params: { path: { id } }, body: input },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  return {
    actionError,
    actionLoading,
    importWorkbook,
    uploadWorkbookFile,
    submitForHrReview,
    hrReview,
    financeReview,
    startPayout,
    refreshHolds,
    rejectBatch,
    cancelBatch,
  };
}

// ─── Rows ────────────────────────────────────────────────────────────────────

export function usePayrollRows() {
  const [data, setData] = useState<{
    rows: ReadonlyArray<PayrollEmployeeRowSummaryDto>;
    total: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(
    async (filter: {
      batchId?: string;
      payoutStatus?: string;
      payoutHoldReason?: string;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number> = {};
        if (filter.batchId) query.batchId = filter.batchId;
        if (filter.payoutStatus) query.payoutStatus = filter.payoutStatus;
        if (filter.payoutHoldReason)
          query.payoutHoldReason = filter.payoutHoldReason;
        if (filter.page) query.page = filter.page;
        if (filter.limit) query.limit = filter.limit;
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/payroll/rows',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchList };
}

export function usePayrollRowActions() {
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const applyHold = useCallback(
    async (
      id: string,
      input: {
        expectedRecordVersion: number;
        reason:
          | 'hr_offboarding_payout_blocked'
          | 'hr_offboarding_retained_blocked'
          | 'hr_offboarding_pending_review'
          | 'certificate_freeze'
          | 'manual_hold';
        source?: string;
        notes?: string;
      },
    ) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/rows/{id}/apply-hold',
          { params: { path: { id } }, body: input },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const releaseHold = useCallback(
    async (id: string, input: { expectedRecordVersion: number; notes?: string }) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/rows/{id}/release-hold',
          { params: { path: { id } }, body: input },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const payRow = useCallback(
    async (
      id: string,
      input: {
        expectedRecordVersion: number;
        paymentMethod:
          | 'bank_transfer'
          | 'cash'
          | 'check'
          | 'wechat'
          | 'alipay'
          | 'other';
        executedAt: string;
        referenceNumber?: string;
        remarks?: string;
        /**
         * Opaque file id returned by `uploadPayoutVoucher`. The backend
         * validates this id is a `voucher` file scoped to the same tenant /
         * batch / row before writing the immutable payout record (Issue #371
         * audit fix). First-phase product decision: optional, because cash
         * and some legacy bank flows may not have a same-day voucher.
         */
        voucherFileId?: string | null;
      },
    ) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/rows/{id}/pay',
          { params: { path: { id } }, body: input },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  /**
   * Issue #371 audit fix — cashier uploads the payout voucher via multipart
   * BEFORE calling `/pay`, then passes the returned `voucherFileId` to
   * `payRow`. The OpenAPI typed client cannot model multipart bodies, so this
   * uses raw `fetch()`; every `res.json()` is preceded by a content-type
   * guard so an HTML proxy / WAF page never gets fed into `JSON.parse`. The
   * bucket / storage key / signed URL must NEVER appear in UI state — only
   * the opaque id and audit metadata returned by the backend.
   */
  const uploadPayoutVoucher = useCallback(
    async (input: {
      rowId: string;
      batchId: string;
      file: File;
    }): Promise<PayrollFileReferenceDto | null> => {
      setActionLoading(true);
      setActionError(null);
      try {
        const form = new FormData();
        form.append('file', input.file, input.file.name);
        form.append('batchId', input.batchId);
        const token = getStoredAccessToken();
        const res = await fetch(
          `/api/v1/finance/payroll/rows/${encodeURIComponent(input.rowId)}/voucher`,
          {
            method: 'POST',
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            body: form,
          },
        );
        if (!res.ok) {
          const status = res.status;
          let message = `HTTP ${status}`;
          if (isJsonContentType(res.headers.get('content-type'))) {
            try {
              const payload: unknown = await res.json();
              if (
                typeof payload === 'object' &&
                payload !== null &&
                'message' in payload &&
                typeof (payload as { message: unknown }).message === 'string'
              ) {
                message = (payload as { message: string }).message;
              }
            } catch {
              // body parse failure; keep status-derived message
            }
          } else {
            message = `付款凭证上传失败 (HTTP ${status})：服务器返回非 JSON 响应`;
          }
          setActionError(toApiError(new Error(message), status));
          return null;
        }
        if (!isJsonContentType(res.headers.get('content-type'))) {
          throw new Error(
            `付款凭证上传失败 (HTTP ${res.status})：服务器未返回 JSON 响应`,
          );
        }
        const ok = (await res.json()) as PayrollFileReferenceDto;
        return ok;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const reveal = useCallback(
    async (
      id: string,
      input: {
        field:
          | 'id_card_number'
          | 'social_insurance_number'
          | 'gross_amount'
          | 'net_amount'
          | 'tax_amount'
          | 'social_insurance_amount'
          | 'housing_fund_amount';
        reason: string;
        businessContext: string;
      },
    ) => {
      setActionLoading(true);
      setActionError(null);
      try {
        const { data: ok, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payroll/rows/{id}/reveal',
          { params: { path: { id } }, body: input },
        );
        if (apiErr) {
          setActionError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return ok ?? null;
      } catch (e) {
        setActionError(toApiError(e));
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  return {
    actionError,
    actionLoading,
    applyHold,
    releaseHold,
    payRow,
    uploadPayoutVoucher,
    reveal,
  };
}
